import { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import type { ColDef, ICellRendererParams, GridReadyEvent } from 'ag-grid-community';
import { RefreshCw, HardDrive, Table2, Rows3, Skull, ExternalLink, Info } from 'lucide-react';
import { fetchTableStats } from '@/api/monitoring';
import type { TableStatEntry } from '@/api/monitoring';
import TableDataModal from './TableDataModal';
import TableSchemaModal from './TableSchemaModal';

/* ── Size bar cell ── */
function SizeBarCell({ value, data }: ICellRendererParams<TableStatEntry>) {
  if (!data) return null;
  const max = (data as any).__maxBytes as number;
  const pct = max > 0 ? Math.round((Number(data.total_size_bytes) / max) * 100) : 0;
  return (
    <div className="flex h-full w-full items-center gap-2">
      <div className="flex-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-blue-400" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-16 shrink-0 text-right text-xs font-medium leading-none text-gray-700">{value}</span>
    </div>
  );
}

function RowCountCell({ value }: ICellRendererParams) {
  return <span className="text-xs font-mono">{Number(value).toLocaleString('tr-TR')}</span>;
}

function DateCell({ value }: ICellRendererParams) {
  if (!value) return <span className="text-gray-300 text-xs">—</span>;
  const d = new Date(value);
  return <span className="text-xs text-gray-600">{d.toLocaleString('tr-TR')}</span>;
}

function DeadCell({ value }: ICellRendererParams) {
  const n = Number(value);
  if (n === 0) return <span className="text-xs text-gray-300">0</span>;
  return <span className={`text-xs font-semibold ${n > 10000 ? 'text-rose-600' : n > 1000 ? 'text-amber-600' : 'text-gray-500'}`}>{n.toLocaleString('tr-TR')}</span>;
}

function buildColDefs(
  maxBytes: number,
  onDetail: (schema: string, table: string) => void,
  onSchema: (schema: string, table: string) => void,
): ColDef<TableStatEntry>[] {
  return [
    {
      field: 'table_name',
      headerName: 'Tablo Adı',
      flex: 2,
      filter: true,
      cellRenderer: ({ value }: ICellRendererParams<TableStatEntry>) => (
        <div className="flex h-full min-w-0 items-center">
          <span className="truncate whitespace-nowrap text-sm font-medium leading-none text-gray-800">{value}</span>
        </div>
      ),
    },
    {
      headerName: '',
      field: 'schema',
      width: 92,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: ({ data }: ICellRendererParams<TableStatEntry>) => (
        <div className="flex h-full items-center justify-center gap-1">
          <button
            onClick={e => { e.stopPropagation(); onSchema(data?.schema ?? 'public', data?.table_name ?? ''); }}
            title="Tablo yapısı"
            className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-violet-50 hover:text-violet-600 transition-colors"
          >
            <Info size={13} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); onDetail(data?.schema ?? 'public', data?.table_name ?? ''); }}
            title="Verileri göster"
            className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
          >
            <ExternalLink size={13} />
          </button>
        </div>
      ),
    },
    {
      field: 'total_size',
      headerName: 'Toplam Boyut',
      width: 220,
      sort: 'desc',
      comparator: (_, __, nodeA, nodeB) =>
        (nodeA.data?.total_size_bytes ?? 0) - (nodeB.data?.total_size_bytes ?? 0),
      cellRenderer: (p: ICellRendererParams<TableStatEntry>) =>
        SizeBarCell({ ...p, data: { ...p.data!, __maxBytes: maxBytes } as any }),
    },
    {
      field: 'table_size',
      headerName: 'Tablo',
      width: 110,
      cellRenderer: ({ value }: ICellRendererParams) =>
        <span className="text-xs text-gray-600">{value}</span>,
    },
    {
      field: 'index_size',
      headerName: 'İndeks',
      width: 110,
      cellRenderer: ({ value }: ICellRendererParams) =>
        <span className="text-xs text-gray-500">{value}</span>,
    },
    {
      field: 'row_count',
      headerName: 'Kayıt Sayısı',
      width: 130,
      filter: 'agNumberColumnFilter',
      cellRenderer: RowCountCell,
    },
    {
      field: 'dead_rows',
      headerName: 'Ölü Satır',
      width: 100,
      filter: 'agNumberColumnFilter',
      cellRenderer: DeadCell,
    },
    {
      headerName: 'Bloat %',
      width: 90,
      valueGetter: ({ data }: { data?: TableStatEntry }) => {
        if (!data) return null;
        const total = data.row_count + data.dead_rows;
        return total > 0 ? Math.round((data.dead_rows / total) * 100) : 0;
      },
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-xs">—</span>;
        const cls = value >= 20 ? 'text-rose-600 font-bold' : value >= 10 ? 'text-amber-600 font-semibold' : 'text-gray-400';
        return <span className={`text-xs ${cls}`}>{value}%</span>;
      },
    },
    {
      headerName: 'Seq Scan %',
      width: 105,
      valueGetter: ({ data }: { data?: TableStatEntry }) => {
        if (!data) return null;
        const total = (data.seq_scan ?? 0) + (data.idx_scan ?? 0);
        return total > 0 ? Math.round(((data.seq_scan ?? 0) / total) * 100) : 0;
      },
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-xs">—</span>;
        const cls = value >= 50 ? 'text-rose-600 font-bold' : value >= 20 ? 'text-amber-600 font-semibold' : 'text-emerald-600';
        return <span className={`text-xs ${cls}`}>{value}%</span>;
      },
    },
    {
      headerName: 'Cache Hit %',
      width: 105,
      valueGetter: ({ data }: { data?: TableStatEntry }) => {
        if (!data) return null;
        const total = (data.heap_blks_hit ?? 0) + (data.heap_blks_read ?? 0);
        return total > 0 ? Math.round(((data.heap_blks_hit ?? 0) / total) * 100) : null;
      },
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-xs">—</span>;
        const cls = value >= 95 ? 'text-emerald-600' : value >= 80 ? 'text-amber-600' : 'text-rose-600 font-bold';
        return <span className={`text-xs font-semibold ${cls}`}>{value}%</span>;
      },
    },
    {
      headerName: 'HOT Upd %',
      width: 105,
      valueGetter: ({ data }: { data?: TableStatEntry }) => {
        if (!data || !data.n_tup_upd) return null;
        return Math.round(((data.n_tup_hot_upd ?? 0) / data.n_tup_upd) * 100);
      },
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-xs">—</span>;
        const cls = value >= 80 ? 'text-emerald-600' : value >= 50 ? 'text-amber-600' : 'text-rose-600';
        return <span className={`text-xs font-semibold ${cls}`}>{value}%</span>;
      },
    },
    {
      field: 'last_autovacuum',
      headerName: 'Son Vacuum',
      width: 160,
      cellRenderer: DateCell,
    },
    {
      field: 'last_autoanalyze',
      headerName: 'Son Analyze',
      width: 160,
      cellRenderer: DateCell,
    },
  ];
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="rounded-xl bg-white shadow-sm p-4 flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-600">{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold leading-none">{value}</p>
      </div>
    </div>
  );
}

export default function TablesTab() {
  const gridRef = useRef<AgGridReact<TableStatEntry>>(null);
  const [tables,      setTables]      = useState<TableStatEntry[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [modal,       setModal]       = useState<{ schema: string; table: string } | null>(null);
  const [schemaModal, setSchemaModal] = useState<{ schema: string; table: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchTableStats();
      setTables(data);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    gridRef.current?.api?.setGridOption('quickFilterText', search);
  }, [search]);

  const onGridReady = useCallback((e: GridReadyEvent) => {
    e.api.sizeColumnsToFit();
  }, []);

  const maxBytes  = tables.length > 0 ? Math.max(...tables.map(t => Number(t.total_size_bytes) || 0)) : 0;
  const totalSize = tables.reduce((s, t) => s + (Number(t.total_size_bytes) || 0), 0);
  const totalRows = tables.reduce((s, t) => s + (Number(t.row_count) || 0), 0);
  const totalDead = tables.reduce((s, t) => s + (Number(t.dead_rows) || 0), 0);

  function formatBytes(bytes: number) {
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
    if (bytes >= 1048576)    return `${(bytes / 1048576).toFixed(1)} MB`;
    if (bytes >= 1024)       return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
  }

  const colDefs = buildColDefs(
    maxBytes,
    (schema, table) => setModal({ schema, table }),
    (schema, table) => setSchemaModal({ schema, table }),
  );

  return (
    <div className="flex flex-col h-full gap-4 p-4 lg:p-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<Table2 size={18} />}    label="Tablo Sayısı" value={tables.length} />
        <StatCard icon={<HardDrive size={18} />} label="Toplam Boyut" value={formatBytes(totalSize)} />
        <StatCard icon={<Rows3 size={18} />}     label="Toplam Kayıt" value={totalRows.toLocaleString('tr-TR')} />
        <StatCard icon={<Skull size={18} />}     label="Ölü Satır"    value={totalDead.toLocaleString('tr-TR')} />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {lastRefresh && <span>Son güncelleme: {lastRefresh.toLocaleTimeString('tr-TR')}</span>}
        </div>
        <input
          type="text"
          placeholder="Tablo adı ile ara..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
        />
        <button
          onClick={load}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50"
        >
          <RefreshCw size={14} /> Yenile
        </button>
      </div>

      {/* Grid */}
      <div className="tables-grid ag-theme-quartz flex-1 min-h-0 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex h-full items-center justify-center text-gray-400 text-sm">Yükleniyor...</div>
        ) : (
          <AgGridReact<TableStatEntry>
            modules={[AllEnterpriseModule]}
            ref={gridRef}
            rowData={tables}
            columnDefs={colDefs}
            defaultColDef={{
              sortable: true,
              resizable: true,
              cellStyle: {
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft: '8px',
                paddingRight: '8px',
              },
            }}
            rowHeight={36}
            onGridReady={onGridReady}
            getRowStyle={(params) => {
              if ((params.data?.dead_rows ?? 0) > 10000) return { background: '#fff1f2' };
              return undefined;
            }}
          />
        )}
      </div>

      {/* Data modal */}
      {modal && (
        <TableDataModal
          schema={modal.schema}
          table={modal.table}
          onClose={() => setModal(null)}
        />
      )}

      {/* Schema modal */}
      {schemaModal && (
        <TableSchemaModal
          schema={schemaModal.schema}
          table={schemaModal.table}
          onClose={() => setSchemaModal(null)}
        />
      )}
    </div>
  );
}
